/**
 * Telemetry shim — emit named events with structured properties.
 *
 * Why a shim and not direct integration:
 *   Xpert doesn't have a chosen analytics SDK yet. This file is a single
 *   call site that adapts to whatever lands later (Posthog, Mixpanel,
 *   Segment, custom). For now it:
 *
 *   - Dispatches a `CustomEvent('xpert:telemetry', {detail: {name, props}})`
 *     on `window`, so any future SDK shim can listen + forward.
 *   - Logs to console in development for visibility.
 *   - Optionally POSTs to /api/telemetry when window.XPERT_TELEMETRY_BEACON
 *     is truthy (off by default, opt-in via a script tag).
 *
 * Spec event names (BR-checkout-ack, §9):
 *   checkout_ack_viewed
 *   checkout_ack_login_prompted
 *   checkout_ack_login_completed
 *   checkout_ack_submitted
 *   checkout_ack_submit_failed
 *   checkout_ack_session_expired
 *
 * Discovery v2 event names (2026-05-26 session — landing + experts + profile):
 *   Landing surface
 *     landing_hero_cta_clicked       { cta: 'primary' | 'secondary', to: string }
 *     landing_tier_cta_clicked       { tag: '24h' | '48h' | '1_week', to: string }  // pricing tier CTAs
 *     landing_featured_expert_clicked { expert_id: string, position: number }  // retired 2026-10-09: section removed (concierge mode)
 *     landing_usecase_clicked        { name: string }  // "Who reviews your work" area cards
 *   Experts directory
 *     experts_results_loaded         { count: number, filters: object }
 *     experts_load_failed            { status: number | null, kind: string }
 *     experts_filter_changed         { key: string, value: string | boolean }
 *     experts_search_used            { query_length: number }   // we don't log the query text
 *     experts_card_clicked           { expert_id: string, position: number }
 *   Expert profile
 *     profile_loaded                 { expert_id: string, verified: boolean, reviews: number }
 *     profile_book_clicked           { expert_id: string, has_slot: boolean }
 *
 * Get Verified v2 event names (2026-05-26 PM session — wizard funnel):
 *   getverified_started               { has_deep_link_category: boolean, resumed: boolean }
 *     - Fires once when the page mounts. `resumed` is true iff a valid
 *       localStorage draft was successfully restored. This is the funnel
 *       entry counter — every later event should be filterable by the
 *       session that started here.
 *   getverified_step_advanced         { from: number, to: number, mode: 'async'|'live'|null }
 *   getverified_step_back             { from: number, to: number }
 *   getverified_signin_link_clicked   { from_step: number }
 *     - The early-auth lever. Returning users who click this avoid
 *       seeing the Account step entirely (auto-skip kicks in when they
 *       reach it as an authed user). Track to measure how often the
 *       lever is actually pulled.
 *   getverified_account_method_chosen { method: 'google'|'email_register'|'email_login' }
 *     - Fires at the moment the user commits to one of the three paths
 *       on the Account step. Google should dominate; if email_password
 *       keeps its lead, our friction-reduction hypothesis is wrong.
 *   getverified_submitted             { mode: 'async'|'live', category: string, turnaround_key: string|null }
 *     - Successful POST /api/submissions OR /api/bookings (depending on mode).
 *   getverified_resumed_from_draft    { restored_step: number, hours_since_save: number }
 *     - Fires when the restore useEffect sets state from a saved draft.
 *
 * Session review v1 event names (2026-05-27 PM session — post-session feedback):
 *   session_review_prompt_viewed   { booking_id: string, hours_since_session: number }
 *     - Fires when the review page mounts with a valid reviewable booking.
 *       hours_since_session lets us measure "how soon after the session do
 *       buyers review?" — important for prompt-timing tuning.
 *   session_review_rating_selected { booking_id: string, rating: 1..5 }
 *     - Fires on each star click. May fire multiple times in one session
 *       (user changes their mind). For funnel analysis use the LAST fire
 *       before session_review_submitted, or count unique (booking_id,user)
 *       to dedupe within a visit.
 *   session_review_submitted       { booking_id: string, rating: 1..5, has_comment: boolean }
 *     - Successful POST /api/reviews. We log has_comment (boolean) but NEVER
 *       the comment text itself — same PII discipline as the discovery
 *       events (no raw free-text in analytics).
 *   session_review_submit_failed   { booking_id: string, status: number|null, kind: string }
 *     - Diagnostic counterpart for submission failures. Pair with the
 *       successful event for funnel completion analysis.
 *   session_review_skipped         { booking_id: string, from_dashboard: boolean }
 *     - Fires when the user clicks "skip for now" rather than submitting.
 *       Distinguish from abandonment (closed-tab-without-clicking) which
 *       can't be observed from this side.
 *
 * Note: we deliberately do NOT log the search query text or note content —
 * those can carry PII or business-sensitive intent. `query_length` is a
 * proxy for "did the user actually type anything" that's enough for the
 * funnel without storing free-text input.
 */

const IS_DEV = typeof process !== "undefined" && process.env?.NODE_ENV === "development";

export function track(name, props = {}) {
  const payload = {
    name,
    props,
    ts: new Date().toISOString(),
  };

  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("xpert:telemetry", { detail: payload }));
    } catch {
      // CustomEvent unavailable (very old browsers) — ignore.
    }

    if (window.XPERT_TELEMETRY_BEACON && navigator?.sendBeacon) {
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        navigator.sendBeacon("/api/telemetry", blob);
      } catch {
        /* sendBeacon failures are silent by design */
      }
    }
  }

  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.debug("[telemetry]", name, props);
  }
}
